import * as XLSX from 'xlsx'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

/** Колонка листа: заголовок и (опционально) ширина в символах. */
export interface ExportColumn {
  title: string
  width?: number
}

/** Лист выгрузки: шапка, строки и (опционально) строка «ИТОГО». */
export interface ExportSheet {
  name: string
  columns: ExportColumn[]
  rows: Array<Array<string | number | null>>
  totals?: Array<string | number | null>
}

/** Как файл в итоге попал к пользователю. */
export type ExportResult = 'downloaded' | 'saved-to-documents'

/** Имя файла без запрещённых символов. */
function safeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '_')
}

/**
 * Выгрузка книги Excel (ARCH-TASK-63).
 *
 * В браузере и Electron файл скачивается, на мобильном (Capacitor) —
 * записывается в каталог «Документы» приложения: системный share-sheet
 * появится отдельной задачей, когда подключим @capacitor/share.
 */
export async function exportWorkbook(
  fileName: string,
  sheets: ExportSheet[]
): Promise<ExportResult> {
  const workbook = XLSX.utils.book_new()

  for (const sheet of sheets) {
    const aoa: Array<Array<string | number | null>> = [
      sheet.columns.map((column) => column.title),
      ...sheet.rows,
    ]
    if (sheet.totals) aoa.push(sheet.totals)

    const worksheet = XLSX.utils.aoa_to_sheet(aoa)
    worksheet['!cols'] = sheet.columns.map((column) => ({
      wch: Math.min(Math.max(column.width ?? 16, 8), 60),
    }))
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name.slice(0, 31))
  }

  const name = safeFileName(fileName)

  if (Capacitor.isNativePlatform()) {
    const base64 = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' })
    await Filesystem.writeFile({ path: name, data: base64, directory: Directory.Documents })
    return 'saved-to-documents'
  }

  const array = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer
  const blob = new Blob([array], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Освобождаем память после старта скачивания.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return 'downloaded'
}
